import { useCallback, useEffect, useRef, useState } from 'react'
import { API_ROUTES, type GuestMe, type JoinResponse } from '@brochure/shared'
import { ApiError, apiFetch } from '@/lib/api'
import { clearGuest, loadGuest, saveGuest, type StoredGuest } from '@/lib/guest'

/** Identità ospite per un torneo: carica/salva il token in `localStorage`, espone join/adopt/forget. */
export function useGuest(tournamentId: string | undefined) {
  const [guest, setGuest] = useState<StoredGuest | null>(() => (tournamentId ? loadGuest(tournamentId) : null))
  const [me, setMe] = useState<GuestMe | undefined>()
  const [isLoading, setIsLoading] = useState(false)
  // Token appena caricato da `adopt`: fa saltare la prossima `reload` (vedi sotto), che altrimenti rifarebbe la stessa GET /me.
  const freshTokenRef = useRef<string | null>(null)
  // Id del torneo già inizializzato (dal lazy state qui sopra): evita di ripetere il reset al primo render.
  const initializedTournamentIdRef = useRef(tournamentId)

  // Cambio di torneo a componente montato: il token dell'ospite precedente non vale per il nuovo, ripescalo dallo storage.
  useEffect(() => {
    if (initializedTournamentIdRef.current === tournamentId) return
    initializedTournamentIdRef.current = tournamentId
    setGuest(tournamentId ? loadGuest(tournamentId) : null)
    setMe(undefined)
    freshTokenRef.current = null
  }, [tournamentId])

  const reload = useCallback(async () => {
    if (!tournamentId || !guest) return
    // `guest` cambia identità a ogni `setGuest` (join/adopt/forget), quindi questo effetto si rilancia sempre:
    // se `adopt` ha già la risposta fresca per questo token, salta il giro invece di rifare la stessa GET /me.
    if (freshTokenRef.current === guest.token) {
      freshTokenRef.current = null
      return
    }
    setIsLoading(true)
    try {
      setMe(await apiFetch<GuestMe>(`${API_ROUTES.tournaments}/${tournamentId}/me`, { guestToken: guest.token }))
    } catch (error) {
      // Token non più valido (ospite rimosso o torneo ripulito): dimentica e torna alla schermata di iscrizione.
      if (error instanceof ApiError && error.status === 401) {
        clearGuest(tournamentId)
        setGuest(null)
        setMe(undefined)
      }
    } finally {
      setIsLoading(false)
    }
  }, [tournamentId, guest])

  useEffect(() => { void reload() }, [reload])

  async function join(name: string, website = '') {
    const res = await apiFetch<JoinResponse>(`${API_ROUTES.tournaments}/${tournamentId}/join`, { method: 'POST', json: { name, website } })
    const stored = { participantId: res.participant.id, token: res.token, name: res.participant.name }
    saveGuest(tournamentId!, stored)
    setGuest(stored)
  }

  /** Link di ricollegamento (?join=): prova il token; se valido lo salva. Un solo giro di rete: la GET /me qui sotto
   * copre sia la validazione del token sia il caricamento di `me`, senza farla ripetere alla `reload` che segue. */
  async function adopt(token: string) {
    const data = await apiFetch<GuestMe>(`${API_ROUTES.tournaments}/${tournamentId}/me`, { guestToken: token })
    const stored = { participantId: data.participant.id, token, name: data.participant.name }
    freshTokenRef.current = token
    saveGuest(tournamentId!, stored)
    setMe(data)
    setGuest(stored)
  }

  function forget() {
    if (tournamentId) clearGuest(tournamentId)
    setGuest(null)
    setMe(undefined)
  }

  return { guest, me, isLoading, join, adopt, forget, reload }
}
